import type { AuthoringBlockNode } from '@personalizehq/schemas';
import { collectIds, isContainer, type ContainerNode } from './tree.js';

// Drop-target resolution — a PURE FUNCTION over rects-as-data.
//
// This is the one piece of editor logic the v1 plan calls out by name: "unit-tested in isolation,
// never through jsdom", because jsdom's getBoundingClientRect() returns all zeros and any test
// written through it asserts against fabricated rects. So the rects arrive as an argument. In
// production they come from `renderer-web`'s snapshot() (the solver's own computed frames — the
// same numbers the SDK will use); in tests they come from a table.
//
// It answers exactly one question, and it is the question the whole no-freeform stance reduces to:
// "which sibling index does this pointer fall between?" There is no x/y answer to give.

/** `[x, y, w, h]`, absolute in surface coordinates — the shape of `SnapshotNode.frame`. */
export type Frame = readonly [number, number, number, number];

export interface DropTargetInput {
  root: AuthoringBlockNode;
  frames: Record<string, Frame>;
  pointer: { x: number; y: number };
  /** The node being dragged. It and its whole subtree are excluded as drop parents — a node cannot
   *  be moved inside itself. Omit when dragging a NEW block in from the palette. */
  draggedId?: string | undefined;
}

export interface DropTarget {
  parentId: string;
  /** Index into `parent.children` AS IT IS NOW (the dragged node still counted, if it is a sibling).
   *  `moveNode` does the same-parent shift. */
  index: number;
  /** The caret line to draw, `[x, y, w, h]`. */
  indicator: Frame;
}

const CARET = 2;

export function resolveDropTarget(input: DropTargetInput): DropTarget | null {
  const { root, frames, pointer, draggedId } = input;
  const excluded = draggedId ? collectIds(pick(root, draggedId) ?? root) : new Set<string>();

  const container = deepestContainerAt(root, frames, pointer, excluded);
  if (!container) return null;

  const frame = frames[container.id];
  if (!frame) return null;

  const index = insertionIndex(container, frames, pointer);
  return {
    parentId: container.id,
    index,
    indicator: caret(container, frames, frame, index),
  };
}

/** Deepest container containing the pointer. Depth-first, children before parent, so a nested stack
 *  wins over the surface root — dropping "into the card" must not mean "into the page". */
function deepestContainerAt(
  node: AuthoringBlockNode,
  frames: Record<string, Frame>,
  pointer: { x: number; y: number },
  excluded: Set<string>
): ContainerNode | null {
  if (excluded.has(node.id)) return null;
  if (!isContainer(node)) return null;

  const frame = frames[node.id];
  if (!frame || !contains(frame, pointer)) return null;

  for (const child of node.children) {
    const deeper = deepestContainerAt(child, frames, pointer, excluded);
    if (deeper) return deeper;
  }
  return node;
}

function insertionIndex(
  container: ContainerNode,
  frames: Record<string, Frame>,
  pointer: { x: number; y: number }
): number {
  const children = container.children;
  if (children.length === 0) return 0;

  // A `box` is a Z-stack: its children are placed by ALIGNMENT, never coordinates, so they overlap
  // and "between which two" has no geometric answer. Order is paint order — append.
  if (container.block === 'box') return children.length;

  if (container.block === 'grid') return gridIndex(children, frames, pointer);

  const axis = mainAxis(container);
  let index = 0;
  for (const child of children) {
    const f = frames[child.id];
    if (!f) continue;
    const mid = axis === 'x' ? f[0] + f[2] / 2 : f[1] + f[3] / 2;
    if ((axis === 'x' ? pointer.x : pointer.y) > mid) index++;
  }
  return index;
}

/** Row-major. Rows are exact in our solver — every cell in a row shares a top edge (SPEC §6.8) —
 *  so grouping by `frame[1]` is a lookup, not a heuristic. */
function gridIndex(
  children: AuthoringBlockNode[],
  frames: Record<string, Frame>,
  pointer: { x: number; y: number }
): number {
  let index = 0;
  for (const child of children) {
    const f = frames[child.id];
    if (!f) continue;
    const rowBottom = f[1] + f[3];
    const midX = f[0] + f[2] / 2;
    const inRowAbove = rowBottom <= pointer.y;
    const inSameRow = !inRowAbove && f[1] <= pointer.y;
    if (inRowAbove || (inSameRow && pointer.x > midX)) index++;
    else if (!inSameRow && !inRowAbove) break; // the pointer is above this row: stop.
  }
  return index;
}

function caret(
  container: ContainerNode,
  frames: Record<string, Frame>,
  containerFrame: Frame,
  index: number
): Frame {
  const axis = container.block === 'grid' ? 'x' : mainAxis(container);
  const children = container.children;

  // Empty, or a box (which appends): a caret between siblings is meaningless. Mark the container.
  if (children.length === 0 || container.block === 'box') {
    return [containerFrame[0], containerFrame[1], containerFrame[2], CARET];
  }

  const before = frames[children[index - 1]?.id ?? ''];
  const at = frames[children[index]?.id ?? ''];

  if (axis === 'x') {
    const x = at ? at[0] : before ? before[0] + before[2] : containerFrame[0];
    const ref = at ?? before ?? containerFrame;
    return [x - CARET / 2, ref[1], CARET, ref[3]];
  }
  const y = at ? at[1] : before ? before[1] + before[3] : containerFrame[1];
  const ref = at ?? before ?? containerFrame;
  return [ref[0], y - CARET / 2, ref[2], CARET];
}

function mainAxis(container: ContainerNode): 'x' | 'y' {
  if (container.block === 'stack') return container.direction === 'row' ? 'x' : 'y';
  if (container.block === 'scroll') return container.axis === 'horizontal' ? 'x' : 'y';
  return 'y';
}

function contains(frame: Frame, p: { x: number; y: number }): boolean {
  return (
    p.x >= frame[0] && p.x <= frame[0] + frame[2] && p.y >= frame[1] && p.y <= frame[1] + frame[3]
  );
}

function pick(root: AuthoringBlockNode, id: string): AuthoringBlockNode | undefined {
  if (root.id === id) return root;
  if (!isContainer(root)) return undefined;
  for (const child of root.children) {
    const found = pick(child, id);
    if (found) return found;
  }
  return undefined;
}
